<script setup lang="ts">
import { storeToRefs } from 'pinia'
import { computed } from 'vue'
import { TooltipProvider, Tooltip, TooltipTrigger, TooltipContent } from '@/components/tooltip'
import { useUserStore } from '../authentication/user.store'
import type { LibraryFolder } from './library_types'

interface Props {
    userId: string,
    avatarSize?: 'sm' | 'md' | 'lg'
    folder?: LibraryFolder
}

const props = withDefaults(defineProps<Props>(), {
    userId: '',
    avatarSize: 'md',
    folder: undefined
})

const avatarSize = computed(() => ({
    'w-6 h-6': props.avatarSize === 'sm', // 24x24
    'w-7 h-7': props.avatarSize === 'md', // 28x28
    'w-8 h-8': props.avatarSize === 'lg' // 32x32
}))

const userStore = useUserStore()
const { getFolderMemberInfo } = storeToRefs(userStore)
const allFolderMembers = computed(() => getFolderMemberInfo.value(props.folder))
const thisFolderMember = computed(() => allFolderMembers.value?.[props.userId] ?? ({
  initials: '?',
  name: 'Unknown user',
  email: '',
  currentUserIsOwner: false,
} as const))
const currentUserIsOwner = computed(() => thisFolderMember.value.currentUserIsOwner)
const thisFolderMemberName = computed(() => thisFolderMember.value.name)
const thisFolderMemberEmail = computed(() => thisFolderMember.value.email)
const thisFolderMemberInitials = computed(() => thisFolderMember.value.initials)
const ariaLabel = computed(() => {
    return `${thisFolderMember.value.isOwner ? 'Owner' : 'Contributor'}: ${currentUserIsOwner.value ? '(Me)' : thisFolderMemberName.value} - Email: ${thisFolderMemberEmail.value}`
})
const additionalClasses = computed(() => {
    return [avatarSize.value, 'border', 'border-blue-500', thisFolderMember.value.isCurrentUser ? 'bg-blue-400 text-white' : 'bg-white text-blue-500']
})

</script>

<template>
    <TooltipProvider
        :delayDuration="200">
        <Tooltip side="bottom">
        <TooltipTrigger asChild>
            <div
                data-testid="avatar"
                class="rounded-full flex items-center justify-center mr-2 text-sm cursor-help outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 fs-exclude"
                tabindex="0"
                :aria-label="ariaLabel"
                :class="additionalClasses">
                {{ thisFolderMemberInitials }}
            </div>
        </TooltipTrigger>
        <TooltipContent class="max-w-[300px] tooltip-content fs-exclude">
            <p>{{ thisFolderMemberName }} {{ currentUserIsOwner ? '(Me) ' : '' }}</p>
            <p>{{ thisFolderMemberEmail }}</p>
        </TooltipContent>
        </Tooltip>
    </TooltipProvider>
</template>
